import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, ArrowRight, RefreshCcw, CheckCircle2, Facebook, Twitter, Mail } from "lucide-react";
import confetti from "canvas-confetti";

type Question = {
  id: string;
  text: string;
  options: { label: string; value: string; addOn: string }[];
};

const questions: Question[] = [
  {
    id: "q1",
    text: "When you look at your kitchen, what bothers you the most?",
    options: [
      { label: "Crumbs hiding in the fridge", value: "fridge", addOn: "Inside Fridge ($45)" },
      { label: "Baked-on grease in the oven", value: "oven", addOn: "Inside Oven ($50)" },
      { label: "Cluttered, disorganized cabinets", value: "cabinets", addOn: "Inside Cabinets ($45)" },
      { label: "A mountain of dirty dishes", value: "dishes", addOn: "Dishes ($25)" },
    ],
  },
  {
    id: "q2",
    text: "What does your ideal weekend look like?",
    options: [
      { label: "Hosting a big dinner party", value: "party", addOn: "Post Party Cleaning ($150)" },
      { label: "Playing with my dogs in the yard", value: "pets", addOn: "Pets ($20)" },
      { label: "Reorganizing my closet", value: "org", addOn: "Organization ($50)" },
      { label: "Catching up on laundry", value: "laundry", addOn: "Laundry and folding ($25)" },
    ],
  },
  {
    id: "q3",
    text: "If you had to inspect one spot in your house right now, what would you check?",
    options: [
      { label: "The baseboards", value: "baseboards", addOn: "Baseboards ($35)" },
      { label: "The window tracks", value: "tracks", addOn: "Interior window tracks ($35)" },
      { label: "The window glass", value: "windows", addOn: "Interior windows ($50)" },
      { label: "The blinds", value: "blinds", addOn: "Wet Wipe Blinds ($35)" },
    ],
  },
];

export const CleaningPersonalityQuiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");

  const handleAnswer = (addOn: string) => {
    const newAnswers = [...answers, addOn];
    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowEmailCapture(true);
    }
  };

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !firstName) return;

    // CRM tracking integration
    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "Onboarding Quiz Lead",
      formData: {
        first_name: firstName,
        email: email,
        "contact.quiz_results": answers.join(", "),
      },
      formLabels: {
        first_name: "First Name",
        email: "Email Address",
        "contact.quiz_results": "Quiz Recommended Add-ons",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
      locationId: "M7JmlFFXdtFNLwyfY61D",
      sessionId: crypto.randomUUID(),
      tags: ["Quiz - Cleaning Habits Report"],
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    setShowEmailCapture(false);
    setShowResults(true);
    triggerConfetti();
  };

  const triggerConfetti = () => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults, particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults, particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const shareText = `I just took the Cleaning Personality Quiz and got my custom home cleaning plan! Find out yours here:`;
  const shareUrl = "https://www.uplevelcleaning.com/quiz-offer";

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowEmailCapture(false);
    setShowResults(false);
    setEmail("");
    setFirstName("");
  };

  return (
    <section className="py-24 bg-primary/5">
      <div className="container max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            <span>Interactive Quiz</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">What's Your Cleaning Personality?</h2>
          <p className="text-lg text-muted-foreground">Take this 3-question quiz to discover the perfect add-ons tailored to your lifestyle.</p>
        </div>

        <Card className="border-2 border-primary/10 shadow-xl bg-white overflow-hidden">
          <CardContent className="p-0">
            {showEmailCapture ? (
              <div className="p-8 md:p-12 text-center bg-gradient-to-br from-white to-primary/5">
                <h3 className="text-3xl font-bold mb-4">Save My Results</h3>
                <p className="text-muted-foreground mb-8 text-lg">Enter your details to save your results, receive your personalized "Cleaning Habits" report, and see your custom recommendations.</p>
                <form onSubmit={handleLeadSubmit} className="max-w-md mx-auto space-y-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full h-14 px-4 rounded-md border border-input bg-background text-lg"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-14 px-4 rounded-md border border-input bg-background text-lg"
                  />
                  <Button type="submit" className="w-full h-14 font-bold text-lg">
                    Save My Results & See Recommendations
                  </Button>
                </form>
              </div>
            ) : !showResults ? (
              <div className="p-8 md:p-12">
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-2 text-sm font-medium text-muted-foreground">
                    <span>Question {currentQuestion + 1} of {questions.length}</span>
                    <span>{Math.round((currentQuestion / questions.length) * 100)}% Completed</span>
                  </div>
                  <Progress value={(currentQuestion / questions.length) * 100} className="h-2" />
                </div>
                
                <h3 className="text-2xl font-bold mb-8">{questions[currentQuestion].text}</h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {questions[currentQuestion].options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(option.addOn)}
                      className="text-left p-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all group"
                    >
                      <div className="font-semibold text-lg group-hover:text-primary transition-colors">
                        {option.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-8 md:p-12 text-center bg-gradient-to-br from-white to-primary/5">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-3xl font-bold mb-4">Your Perfect Add-on Bundle</h3>
                <p className="text-primary font-medium mb-4">✨ Your personalized Cleaning Habits report has been sent to your email!</p>
                <p className="text-muted-foreground mb-8 text-lg">Based on your answers, we highly recommend adding these specialized services to your next clean:</p>
                
                <div className="max-w-md mx-auto space-y-3 mb-10 text-left">
                  {answers.map((addOn, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border border-border">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                      <span className="font-bold text-lg">{addOn}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                  <Button size="lg" className="font-bold text-lg px-8 h-14 w-full sm:w-auto btn-pulse" onClick={() => document.dispatchEvent(new Event('open-booking'))}>
                    Book with Add-ons <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button variant="outline" size="lg" className="h-14 w-full sm:w-auto" onClick={resetQuiz}>
                    <RefreshCcw className="mr-2 w-4 h-4" /> Retake Quiz
                  </Button>
                </div>

                <div className="border-t pt-6">
                  <p className="text-sm font-medium text-muted-foreground mb-4">Share your results with friends!</p>
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" size="icon" className="rounded-full" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}>
                      <Facebook className="w-5 h-5 text-blue-600" />
                    </Button>
                    <Button variant="outline" size="icon" className="rounded-full" onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank')}>
                      <Twitter className="w-5 h-5 text-sky-500" />
                    </Button>
                    <Button variant="outline" size="icon" className="rounded-full" onClick={() => window.open(`mailto:?subject=My Cleaning Personality&body=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank')}>
                      <Mail className="w-5 h-5 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
